import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Wifi, Video, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://api.allbaise.com"}/api`;

/**
 * DronePublicViewer — public page for viewing a drone live stream.
 * No login required. Access via /drone/view/{token}
 */
export default function DronePublicViewer() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const videoRef = useRef(null);
  const loadingRef = useRef(true);

  // Fetch session info (polling to catch live status changes)
  useEffect(() => {
    if (!token) return;
    let interval;

    const load = async () => {
      try {
        const res = await fetch(`${API}/drone/public/${token}`);
        if (!res.ok) throw new Error(res.status === 404 ? "Stream not found" : "Failed to load");
        setInfo(await res.json());
      } catch (err) {
        if (loadingRef.current) setError(err.message);
      }
      if (loadingRef.current) {
        loadingRef.current = false;
        setLoading(false);
      }
    };

    load();
    interval = setInterval(load, 5000);

    return () => clearInterval(interval);
  }, [token]);

  // Stream via relay segments, or fallback to backend snapshot
  useEffect(() => {
    if (!info || !info.stream_name || !videoRef.current) return;
    const video = videoRef.current;
    const RELAY = "https://stream-relay-production.up.railway.app";
    const streamName = info.stream_name;
    const relayBase = RELAY + "/hls/" + encodeURIComponent(streamName);
    // snapshot_url comes from backend (points to relay OR ingest endpoint)
    const snapUrl = info.snapshot_url || RELAY + "/hls/" + encodeURIComponent(streamName) + "/snapshot.webm";
    let closed = false;

    let ms = null, sb = null, seen = new Set(), pollTimer = null, fallbackTimer = null;
    let pendingQ = null;
    let isSegmentsMode = false;
    const drainQ = () => { while (pendingQ && pendingQ.length > 0 && sb && !sb.updating && !closed) { try { sb.appendBuffer(pendingQ.shift()); } catch (_) {} } };

    const fetchSegments = async () => {
      try {
        const resp = await fetch(relayBase + "/segments");
        if (!resp.ok) { return false; }
        const d = await resp.json();
        if (d.count === 0) return false; // No segments on relay
        isSegmentsMode = true;
        for (const id of d.segmentIds || []) {
          if (seen.has(id)) continue;
          seen.add(id);
          try {
            const r2 = await fetch(relayBase + "/segment/" + id);
            if (!r2.ok) continue;
            const buf = await r2.arrayBuffer();
            if (!buf || buf.byteLength < 50) continue;
            if (!sb || sb.updating) { if (!pendingQ) pendingQ = []; pendingQ.push(buf); }
            else { try { sb.appendBuffer(buf); } catch (_) { if (!pendingQ) pendingQ = []; pendingQ.push(buf); } }
          } catch (_) {}
        }
        return true;
      } catch (_) { return false; }
    };

    const startSegments = async () => {
      const hasSegments = await fetchSegments();
      if (!hasSegments) { startFallback(); return; }

      ms = new MediaSource();
      ms.onsourceopen = async () => {
        if (closed) return;
        try { sb = ms.addSourceBuffer("video/webm;codecs=vp8,opus"); }
        catch (_) { try { sb = ms.addSourceBuffer("video/webm"); } catch (_2) { return; } }
        sb.mode = "sequence";
        sb.onupdateend = () => { drainQ(); if (!closed && video && video.paused) video.play().catch(() => {}); };
        drainQ();
        await fetchSegments();
        if (video.paused) video.play().catch(() => {});
        if (info && !info.is_live) setInfo(prev => ({...prev, is_live: true}));
      };
      video.src = URL.createObjectURL(ms);
      pollTimer = setInterval(async () => {
        if (closed || !isSegmentsMode) return;
        await fetchSegments();
      }, 2000);
    };

    // Fallback: try relay snapshot first, then backend ingest
    const startFallback = () => {
      if (info && !info.is_live) setInfo(prev => ({...prev, is_live: true}));
      const relaySnap = RELAY + "/hls/" + encodeURIComponent(streamName) + "/snapshot.webm";
      const trySnap = (url) => { video.src = url; video.play().catch(() => {}); video.onended = () => { video.src = url; video.play().catch(() => {}); }; };
      // Try relay snapshot first (WebSocket broadcasts), fallback to backend (HTTP ingest)
      fetch(relaySnap, { method: "HEAD" }).then(r => { trySnap(r.ok ? relaySnap : snapUrl); }).catch(() => trySnap(snapUrl));
      fallbackTimer = setInterval(() => { if (video && video.ended) { video.src = relaySnap; video.play().catch(() => {}); } }, 5000);
    };

    // Start: try segments first, fails fast if no segments
    startSegments();

    return () => {
      closed = true;
      if (pollTimer) clearInterval(pollTimer);
      if (fallbackTimer) clearInterval(fallbackTimer);
      if (ms && ms.readyState === "open") try { ms.endOfStream(); } catch (_) {}
      if (video) { video.onended = null; video.removeAttribute("src"); }
    };
  }, [info && info.stream_name ? info.stream_name : null]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-400 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">Loading stream...</p>
        </div>
      </div>
    );
  }

  if (error || !info) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <Video className="w-12 h-12 text-slate-700 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-white mb-2">Stream Not Available</h1>
          <p className="text-slate-400 text-sm mb-6">{error || "This stream does not exist or has ended."}</p>
          <Link to="/" className="text-cyan-400 hover:text-cyan-300 text-sm underline">Go to AllBaise</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col">
      {/* Minimal header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <Link to="/" className="text-white font-bold text-sm">AllBaise</Link>
          <span className="text-slate-600">|</span>
          <span className="text-sm text-white font-medium">{info.name || "Drone Feed"}</span>
          <span className={`text-xs px-1.5 py-0.5 rounded ${
            info.is_live ? "bg-emerald-600 text-white" : "bg-slate-700 text-slate-400"
          }`}>
            {info.is_live ? "LIVE" : "OFFLINE"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">{info.drone_model || "Drone"}</span>
          {info.is_live && <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />}
        </div>
      </div>

      {/* Video player — always rendered so videoRef works for snapshot polling */}
      <div className="flex-1 flex items-center justify-center bg-black relative">
        <video
          ref={videoRef}
          controls
          muted
          autoPlay
          playsInline
          className="w-full h-full object-contain"
          style={{ maxHeight: "85vh", background: "#000" }}
        />
        {!info.is_live && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-10">
            <div className="text-center">
              {info.ended ? (
                <>
                  <Video className="w-12 h-12 text-red-600 mx-auto mb-3" />
                  <p className="text-slate-300 text-sm mb-1">Stream Ended</p>
                  <p className="text-slate-500 text-xs">The broadcast has stopped or timed out</p>
                </>
              ) : (
                <>
                  <Wifi className="w-12 h-12 text-slate-700 mx-auto mb-3" />
                  <p className="text-slate-400 text-sm mb-1">Waiting for drone feed...</p>
                  <p className="text-slate-600 text-xs">The stream will start automatically when the drone connects</p>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-slate-800 flex items-center justify-between">
        <p className="text-[10px] text-slate-600">
          Share this link for others to watch: <a href={window.location.href} className="text-cyan-600 hover:text-cyan-500">{window.location.href}</a>
        </p>
        <Link to="/" className="text-[10px] text-slate-500 hover:text-slate-400 flex items-center gap-1">
          <ExternalLink className="w-3 h-3" /> AllBaise
        </Link>
      </div>
    </div>
  );
}
